import { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { useProperties } from "../../../contexts/properties/PropertiesContext";
import { Datepicker } from "@zendeskgarden/react-datepickers";
import css from "./styles/payrollDatePicker.module.scss";
import { MONTHS } from "../../../constants/Months";
import { getAcquisitionPropertyVersionForDisplayDate } from "../../../pages/workflows/account/formula-bar/logic/utils";
import { ensureDateInLocale, ensureDateInUtc } from "../date-helpers";
import { CalendarTodayTwoTone } from "@material-ui/icons";
import { TooltipModal } from "@zendeskgarden/react-modals";
import MonthPicker, { MonthPickerType } from "../../../pages/workflows/account/formula-bar/components/month-picker/MonthPicker";
import { Input } from "@zendeskgarden/react-forms";
import { DateFormatter } from "../helpers/utils";
import cn from "classnames";


export interface IPayrollDatePickerProps {
    minDate?: string;
    maxDate?: string;
    dateValue?: string | null;
    disabled?: boolean;
    suppressDefaultPicker?: boolean;
    onDateChange: (date: string) => void;
    onBlur?: () => void;
}

export default function PayrollDatePicker({
    minDate,
    maxDate,
    dateValue,
    disabled,
    suppressDefaultPicker,
    onDateChange,
    onBlur,
}: IPayrollDatePickerProps): ReactElement {
    const {currentProperty} = useProperties();

    // these variables are only used in acquisition property month picker
    const dateTriggerRef = useRef<HTMLParagraphElement>(null);
    const [dateRefElement, setDateRefElement] = useState<HTMLParagraphElement | null>(null);
    const [minMonth, setMinMonth] = useState<number>(0);
    const [minYear, setMinYear] = useState<number>(1986);
    const [maxMonth, setMaxMonth] = useState<number>(11);
    const [maxYear, setMaxYear] = useState<number>(2100);
    const [dateMonth, setDateMonth] = useState<number>(0);
    const [dateYear, setDateYear] = useState<number>(1986);

    const dateText = useMemo<string>(() => {
        if (!dateValue) {
            return "None";
        }

        if (currentProperty?.isAcquisitionProperty) {
            const displayYear = getAcquisitionPropertyVersionForDisplayDate(dateMonth, dateYear, currentProperty);

            return `${MONTHS[dateMonth]} ${displayYear}`;
        }
        return "";
    }, [dateMonth, dateYear, currentProperty]);

    useEffect(() => {
        if (currentProperty?.isAcquisitionProperty &&
            currentProperty.acquisitionBudgetYear != null &&
            currentProperty.acquisitionBudgetStartMonth != null
        ) {
            const defaultMinMonth = currentProperty.acquisitionBudgetStartMonth;
            const defaultMinYear = defaultMinMonth == 0 ? currentProperty.acquisitionBudgetYear - 2 : currentProperty.acquisitionBudgetYear - 1;
            const defaultMaxMonth = defaultMinMonth == 0 ? 11 : defaultMinMonth - 1;
            const defaultMaxYear = defaultMinMonth == 0 ? currentProperty.acquisitionBudgetYear : currentProperty.acquisitionBudgetYear + 1;

            const startDate = ensureDateInLocale(minDate);
            const endDate = ensureDateInLocale(maxDate);

            if (dateValue) {
                const date = ensureDateInLocale(dateValue);

                if (date) {
                    setDateMonth(date.getMonth());
                    setDateYear(date.getFullYear());
                }
            } else {
                setDateMonth(defaultMinMonth);
                setDateYear(defaultMinYear);
            }

            if (startDate) {
                setMinMonth(startDate.getMonth());
                setMinYear(startDate.getFullYear());
            } else {
                setMinMonth(defaultMinMonth);
                setMinYear(defaultMinYear);
            }

            if (endDate) {
                setMaxMonth(endDate.getMonth());
                setMaxYear(endDate.getFullYear());
            } else {
                setMaxMonth(defaultMaxMonth);
                setMaxYear(defaultMaxYear);
            }
        }
    }, [currentProperty, minDate, maxDate, dateValue]);

    if (currentProperty?.isAcquisitionProperty) {
        return(
            <div className={css.monthPicker}>
                <p
                    className={cn(css.monthPickerValueText, disabled === true && css.isDisabled)}
                    ref={dateTriggerRef}
                    onClick={() => {
                        if (disabled !== true) {
                            setDateRefElement(dateTriggerRef.current);
                        }
                    }}
                >
                    {dateText} <CalendarTodayTwoTone />
                </p>
                <TooltipModal
                    className={css.monthPickerTooltipContainer}
                    referenceElement={dateRefElement}
                    onClose={() => setDateRefElement(null)}
                    isAnimated
                >
                    <TooltipModal.Body>
                        <MonthPicker
                            minMonth={minMonth}
                            maxMonth={maxMonth}
                            minYear={minYear}
                            maxYear={maxYear}
                            defaultMonth={dateMonth}
                            defaultYear={dateYear}
                            setMonth={(month, year) => {
                                setDateMonth(month);
                                setDateYear(year);
                                setDateRefElement(null);
                                const dateString = `${year}-${month + 1}-01`;
                                onDateChange(dateString);
                            }}
                            setYear={() => null}
                            type={MonthPickerType.FROM}
                            property={currentProperty}
                        />
                    </TooltipModal.Body>
                </TooltipModal>
            </div>
        );
    }

    if (suppressDefaultPicker !== false) {
        return (
            <Datepicker
                value={dateValue ? ensureDateInLocale(dateValue) : undefined}
                onChange={(selectedStartDate) => {
                    onDateChange(ensureDateInUtc(selectedStartDate)?.toISOString() ?? "");
                }}
                formatDate={date => {
                    return date ? DateFormatter.format(date) : "";
                }}
            >
                <Input disabled={disabled === true} onBlur={onBlur} />
            </Datepicker>
        );
    }

    return <></>;
}
